'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { useGSAPContext, useSanityFilterSync } from '@/hooks'
import { useListScrollRestore } from '@/hooks/use-list-scroll-restore'
import { cn } from '@/lib/utils'
import {
  Button,
  CardMain,
  Container,
  Section,
  SectionHeaderContent,
  SpotSanityError,
  SpotSanityFilters
} from '@/ui/components'
import {
  getGroupedFacets,
  getMatchedFacets,
  getTitleTag,
  sorting
} from '@/utils'
import { ScrollTrigger, gsap } from '@gsap'
import { memo, useEffect, useRef, useState } from 'react'

import { useStoreI18n, useStoreTour } from '@/store'
import { Typo3SectionSpotSanityTour, Typo3TourItem } from '@/types/typo3'
import type { ContentFacets } from '@/utils/filters/get-grouped-facets'

interface Props {
  data: Typo3SectionSpotSanityTour
}

const List = ({ data }: Props) => {
  const D = useStoreI18n.use.dictionary()

  const c = data?.content
  const limit = data?.plugin?.limitItems || 9

  const didMountRef = useRef(false)
  const firstAnimSkipRef = useRef(true)
  const ref = useRef<HTMLElement>(null)
  const eventsRef = useRef<HTMLDivElement>(null)
  const tl = useRef<GSAPTimeline | null>(null)

  const [animate, setAnimate] = useState(false)
  const [filteredItems, setFilteredItems] = useState<Typo3TourItem[]>([])
  const [delayedItems, setDelayedItems] = useState(filteredItems)

  //stores
  const store = useStoreTour()

  const {
    facets,
    setFacets,
    setStoreItemsToShow,
    setStoreFilteredItems,
    startDate,
    endDate,
    isOpenToday,
    setIsOpenToday,
    setIsReady,
    search,
    distance,
    duration,
    sortBy,
    setDistance,
    setDuration,
    setSearch,
    difficulty,
    setDifficulty,
    reset
  } = store

  //animations
  useGSAPContext({
    scope: eventsRef,
    callback() {
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: EASE.out, duration: DUR.default / 2 }
      })
      tl.current.to(eventsRef.current, { opacity: 0 })
    }
  })

  //Set ditance min max
  useEffect(() => {
    if (c?.items && c?.items.length > 0) {
      // Convert distances to numbers and filter out invalid distances
      const validDistances = c.items
        .map((item) => Number(item.distance))
        .filter((distance) => !isNaN(distance))

      if (validDistances.length > 0) {
        // Calculate the minimum and maximum distances
        const min = Math.min(...validDistances)
        const max = Math.max(...validDistances)

        // Update the state
        setDistance({
          min: min,
          max: max,
          selected: null
        })
      }
      const minDuration = Math.min(
        ...c.items.map((item) => Number(item.duration))
      )
      const maxDuration = Math.max(
        ...c.items.map((item) => Number(item.duration))
      )

      setDuration({
        min: minDuration,
        max: maxDuration,
        selected: null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.items])

  useSanityFilterSync({
    data,
    setFacets,
    setIsOpenToday,
    setSearch,
    setDifficulty,
    facets,
    search,
    isOpenToday,
    difficulty
  })

  // Filter items based on facets
  useEffect(() => {
    // Group facets by prefix
    const groupedFacets = getGroupedFacets(
      facets,
      c?.facets as unknown as ContentFacets
    )

    // Combine facet and date range filtering
    const filtered = c?.items?.filter((item) => {
      // Facet filtering
      const matchesFacets = getMatchedFacets(facets, groupedFacets, item)
      const todayOpen = isOpenToday ? !item?.isStatusClosed : true

      //Sliders Filter
      const distanceMatch = distance.selected
        ? item?.distance &&
          Number(item.distance) >= distance.selected[0] &&
          Number(item.distance) <= distance.selected[1]
        : true

      const durationMatch = duration.selected
        ? item?.duration &&
          Number(item.duration) >= duration.selected[0] &&
          Number(item.duration) <= duration.selected[1]
        : true

      // Difficulty filter (derived from item field, not a TYPO3 facet)
      const difficultyMatch =
        difficulty.length > 0
          ? difficulty.includes(item?.difficulty ?? '')
          : true

      const searchMatch =
        search && search.trim() !== ''
          ? item?.title?.toLowerCase().includes(search.toLowerCase()) ||
            item?.summary?.toLowerCase().includes(search.toLowerCase())
          : true

      // Item must match both facet and date range criteria
      return (
        matchesFacets &&
        searchMatch &&
        todayOpen &&
        distanceMatch &&
        durationMatch &&
        difficultyMatch
      )
    })

    //Sorting
    const sorted = sorting(filtered, sortBy)

    setFilteredItems(sorted as Typo3TourItem[])
  }, [
    c,
    facets,
    startDate,
    endDate,
    isOpenToday,
    search,
    sortBy,
    distance,
    duration,
    difficulty
  ])

  // common for all lists
  useEffect(() => {
    if (!didMountRef.current && filteredItems?.length > 0) {
      didMountRef.current = true
      setDelayedItems(filteredItems)
      return
    }

    setStoreFilteredItems(filteredItems?.length)

    const timer = setTimeout(() => setDelayedItems(filteredItems), 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems])

  const handleLoadMore = () => {
    gsap.to(eventsRef.current, {
      duration: DUR.default / 2,
      opacity: 0,
      onComplete: () => {
        setItemsToShow((prev) => Math.min(prev + limit, filteredItems?.length))
        gsap.to(eventsRef.current, { duration: DUR.default / 2, opacity: 1 })
        gsap.delayedCall(0.25, () => ScrollTrigger.refresh())
      }
    })
  }
  const { saveItemsCount, getSavedItemsCount } = useListScrollRestore({
    onLoadMore: handleLoadMore
  })

  const [itemsToShow, setItemsToShow] = useState(() => {
    try {
      const saved = getSavedItemsCount()
      return saved || limit
    } catch {
      return limit
    }
  })

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreFilteredItems(filteredItems.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems.length])

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreItemsToShow(itemsToShow)
    saveItemsCount(itemsToShow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToShow])

  //handlers
  useEffect(() => {
    if (firstAnimSkipRef.current) {
      firstAnimSkipRef.current = false
      return
    }
    setAnimate(true)
    const timer = setTimeout(() => setAnimate(false), 500)
    return () => clearTimeout(timer)
  }, [filteredItems])

  useEffect(() => {
    if (animate) {
      tl.current?.play()
    } else {
      tl.current?.reverse()
    }
  }, [animate])

  //reset all filters if noFilter component is present
  useEffect(() => {
    if (data?.plugin?.hasShowFilter === false) {
      reset()
      setIsReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.plugin?.hasShowFilter])

  return (
    <Section
      data={data}
      className={'s-spotsanity-list-tour'}
      anim="fade-in"
      ref={ref}
    >
      <SectionHeaderContent
        titleLayout={c?.titleLayout}
        as={getTitleTag(c?.titleLayout)}
      >
        {c?.title}
      </SectionHeaderContent>
      <Container>
        {c?.buttons && c?.buttons?.length > 0 && (
          <div className="gap-gutter mt-xs mb-lg flex">
            {c?.buttons.map((button, index) => (
              <Button {...button} key={button?.label || index}>
                {button?.label}
              </Button>
            ))}
          </div>
        )}
        {data.plugin?.hasShowFilter && (
          <SpotSanityFilters data={data} store={useStoreTour} />
        )}

        <div
          className={cn(
            'gap-x-xs gap-y-lg grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          )}
          ref={eventsRef}
        >
          {delayedItems.slice(0, itemsToShow).map((item) => (
            <CardMain
              title={item.title}
              description={item.summary}
              link={item.link}
              image={item.images?.[0]}
              todayOpen={item.liveStatus === 'open' ? 'open' : 'closed'}
              distance={item.distance}
              key={item.uid}
              variant="tour"
              tourInfo={{
                distance: item.distanceFormatted,
                duration: item.durationFormatted,
                altitudeUphill: item.maxAltitude,
                altitudeDownhill: item.minAltitude,
                difficulty: item?.difficulty,
                isStatusClosed: item?.isStatusClosed,
                isStatusOpen: item?.isStatusOpen,
                difficultyLabel: item?.difficultyLabel
              }}
            />
          ))}
        </div>
        <div>
          {delayedItems && itemsToShow < filteredItems?.length && (
            <div className="mt-lg flex justify-center">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                nextLink={false}
                as="button"
                aria-label={`Load more items. Currently showing ${itemsToShow} of ${filteredItems?.length} items`}
              >
                {D['fe.plugin.filters.loadMore']}
              </Button>
            </div>
          )}
          {delayedItems && delayedItems?.length === 0 && (
            <SpotSanityError store={store} />
          )}
        </div>
      </Container>
    </Section>
  )
}

export default memo(List)
